import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { TEXTURES } from '@/lib/assets';

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps): JSX.Element {
  return (
    <main className="relative min-h-dvh flex items-center justify-center px-4 py-10">
      {/* Atmospheric background */}
      <img
        src="/art/backgrounds/dock_at_night.webp"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/60 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-b from-sea-deep/40 via-transparent to-transparent" />

      <div className="relative w-full max-w-md stage-fade-in">
        {/* Saltbound title link */}
        <div className="text-center mb-5">
          <Link
            to="/"
            className="font-display text-3xl text-parchment tracking-wide hover:text-candle transition-colors drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
          >
            Saltbound
          </Link>
        </div>

        {/* Parchment card */}
        <div
          className="relative overflow-hidden rounded-lg border-2 border-wood-mid/60 shadow-[0_8px_32px_rgba(0,0,0,0.6)]"
          style={{
            backgroundImage: `url("${TEXTURES.parchment}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Warm overlay for readability */}
          <div className="absolute inset-0 bg-parchment/85 pointer-events-none" />

          <div className="relative">
            <div className="px-6 sm:px-8 pt-8 pb-5 border-b-2 border-wood-mid/25">
              <h1 className="font-display text-3xl text-wood-dark mb-1">{title}</h1>
              {subtitle && <p className="text-ink-mid text-sm leading-snug">{subtitle}</p>}
            </div>

            <div className="px-6 sm:px-8 py-6">{children}</div>

            {footer && (
              <div className="px-6 sm:px-8 pb-6 pt-2 text-sm text-ink-mid text-center">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
